import type { JSX } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Callout } from './callout-extension';
import { BlockHandle } from './block-handle';
import { MediaEmbed } from './media-embed';
import { ResizableImage } from './resizable-image';
import { ImageGallery } from './image-gallery';
import { FkTable } from './fk-table';
import { FkVideo } from './video-node';
import { FkColumn, FkColumns, FkStep, FkSteps, FkToggle } from './layout-nodes';
import { FkButton, FkCheck, FkEmbed, FkFile, FkKpi, FkTab, FkTabs } from './misc-nodes';
import { SlashCommands, type SlashPickDiskFilesFn, type SlashUploadFn } from './slash-commands';
import { buildLinkPasteTransaction } from './paste-links';
import { DiskFilePickerModal } from '../components/DiskFilePickerModal';
import type { PickedDiskFile } from '../lib/api/kb';
import { TiptapBubbleMenu, CalloutBubbleMenu } from './TiptapBubbleMenu';
import { TiptapToolbar } from './TiptapToolbar';
import { seedCollabDoc, type CollabSession } from './useCollab';
import type { EditorView } from '@tiptap/pm/view';

/** Достаёт картинки-файлы из буфера обмена / drag-n-drop (для paste/drop). */
function imageFilesFrom(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const out: File[] = [];
  for (const item of Array.from(dt.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const f = item.getAsFile();
      if (f) out.push(f);
    }
  }
  // Fallback: некоторые браузеры кладут картинку только в dt.files.
  if (out.length === 0) {
    for (const f of Array.from(dt.files)) {
      if (f.type.startsWith('image/')) out.push(f);
    }
  }
  return out;
}

/**
 * Грузит вставленные/дропнутые картинки через uploadFile (attachments статьи)
 * и вставляет их в документ как <img src=ссылка>, а не base64. Так bodyMd не
 * раздувается и не упирается в лимит 200 000 символов.
 */
async function uploadAndInsert(
  view: EditorView,
  files: File[],
  uploadFile: SlashUploadFn,
): Promise<void> {
  for (const file of files) {
    try {
      const r = await uploadFile(file);
      if (!r?.url) continue;
      const node = view.state.schema.nodes.image.create({ src: r.url });
      view.dispatch(view.state.tr.replaceSelectionWith(node));
    } catch {
      /* загрузка не удалась — молча пропускаем (без base64-фоллбэка) */
    }
  }
}

/**
 * Tiptap-редактор статей wiki — клон Teamly UI.
 *
 * Единственный редактор статей: инлайн-правка на /article/:slug (?edit=1).
 * Хранит HTML внутри ProseMirror, при сохранении конвертим в markdown
 * через turndown (см. utils/htmlToMarkdown.ts) — vault остаётся .md.
 *
 * Возможности:
 *  - StarterKit: heading, bold, italic, strike, code, blockquote, lists,
 *    horizontal-rule, code-block.
 *  - Underline + Highlight (выделение цветом).
 *  - Link с автоматической conversion URL.
 *  - Image (ссылка на /api/wiki/assets/...).
 *  - TaskList с чек-боксами `[ ]`.
 *  - Table с resizable колонками.
 *  - Slash-меню `/` для вставки блоков (как в Teamly/Notion).
 *  - Bubble-меню при выделении (форматирование текста).
 *
 * Стилизация в frontend-wiki/src/editor/editor.css — приоритет на
 * brand-цвета, шрифт как в CRM.
 */
export interface TiptapEditorProps {
  /** Initial HTML или пустая строка для нового документа. */
  initialHtml: string;
  /** Вызывается при каждом изменении (debounced снаружи если нужно). */
  onChange: (html: string) => void;
  /** Placeholder когда документ пуст. */
  placeholder?: string;
  /** Read-only режим (для preview). */
  editable?: boolean;
  /** Сколько секунд autosave (если 0 — не autosave). */
  autosaveSec?: number;
  /** Callback при autosave. */
  onAutosave?: (html: string) => void;
  /** Если задан — slash-блоки «Изображение»/«Файл» грузят файл с компьютера
   *  (через attachments статьи). Без него — спрашиваем URL. */
  uploadFile?: SlashUploadFn;
  /**
   * Окно выбора файлов Диска для пункта «Файл с Диска». По умолчанию редактор показывает своё
   * (DiskFilePickerModal) — проп нужен, только чтобы подменить окно.
   */
  pickDiskFiles?: SlashPickDiskFilesFn;
  /**
   * Совместное редактирование (фаза 4.7). Передавать ТОЛЬКО сессию в
   * состоянии `ready` — с живым Y.Doc и провайдером (см. useCollab).
   * Тогда текст берётся из Y.Doc, а не из initialHtml, историю ведёт
   * y-undo (общая на всех), и рядом видны курсоры соредакторов.
   * Без пропа редактор работает как раньше — локально.
   */
  collab?: CollabSession;
  /**
   * Даёт наружу инстанс редактора: нужен, чтобы заменить текст командой
   * (AI-перепись по стайлгайду) — при collab через setEditHtml это делать
   * нельзя, правку должны увидеть все участники.
   */
  onEditorReady?: (editor: Editor | null) => void;
}

export function TiptapEditor({
  initialHtml,
  onChange,
  placeholder = "Напишите что-нибудь или введите «/» для вставки блока…",
  editable = true,
  autosaveSec = 0,
  onAutosave,
  uploadFile,
  pickDiskFiles: pickDiskFilesProp,
  collab,
  onEditorReady,
}: TiptapEditorProps): JSX.Element {
  // «Файл с Диска»: окно выбора открывает сам редактор. Функция стабильна (useCallback), иначе набор
  // расширений ниже пересобирался бы на каждый рендер. Промис ждёт выбора и резолвится списком файлов.
  const [diskPicker, setDiskPicker] = useState<{ resolve: (files: PickedDiskFile[]) => void } | null>(null);
  const diskPickerRef = useRef(diskPicker);
  diskPickerRef.current = diskPicker;
  const finishDiskPicker = useCallback((files: PickedDiskFile[]) => {
    diskPickerRef.current?.resolve(files);
    setDiskPicker(null);
  }, []);
  const ownPickDiskFiles = useCallback<SlashPickDiskFilesFn>(
    () =>
      new Promise<PickedDiskFile[]>((resolve) => {
        diskPickerRef.current?.resolve([]); // прошлое окно ещё висело — закрываем без выбора
        setDiskPicker({ resolve });
      }),
    [],
  );
  const pickDiskFiles = pickDiskFilesProp ?? ownPickDiskFiles;

  // Realtime включён только когда сессия реально подключилась. Разбираем
  // сессию на отдельные поля: сам объект useCollab отдаёт новым на каждом
  // рендере, а ydoc/provider/user стабильны — иначе набор расширений ниже
  // пересобирался бы на каждое нажатие клавиши.
  const liveYdoc = collab?.state === 'ready' ? collab.ydoc : null;
  const liveProvider = collab?.state === 'ready' ? collab.provider : null;
  const liveExtensions = collab?.extensions;
  const live = liveYdoc && liveProvider;
  // Текст для засева пустого Y.Doc — initialHtml НА МОМЕНТ МОНТИРОВАНИЯ, не «свежий»: при подключении
  // y-prosemirror рисует пустой Y.Doc и шлёт onUpdate('<p></p>'), родитель кладёт его обратно в
  // initialHtml, и засев через 0,7 с подставил бы пустоту вместо статьи (владелец 16.10.2026:
  // «Редактировать» — пустой редактор). Collab-редактор монтируется заново (key), так что значение свежее.
  const seedHtmlRef = useRef(initialHtml);
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        // Своя история несовместима с общей: undo должен откатывать ТОЛЬКО
        // свои правки, а не чужие. Это делает yUndoPlugin из Collaboration —
        // он же подменяет команды undo/redo, поэтому кнопки тулбара работают.
        history: liveExtensions?.length ? false : undefined,
        heading: { levels: [1, 2, 3, 4] },
        codeBlock: {
          HTMLAttributes: { class: 'wiki-code-block' },
        },
        bulletList: {
          HTMLAttributes: { class: 'wiki-bullet-list' },
        },
        orderedList: {
          HTMLAttributes: { class: 'wiki-ordered-list' },
        },
        blockquote: {
          HTMLAttributes: { class: 'wiki-blockquote' },
        },
      }),
      Underline,
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: false, // открываем сами через React Router если slug
        autolink: true,
        HTMLAttributes: { class: 'wiki-link' },
      }),
      ResizableImage,
      TaskList.configure({
        HTMLAttributes: { class: 'wiki-task-list' },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: { class: 'wiki-task-item' },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: { class: 'wiki-table' },
      }),
      TableRow,
      TableHeader,
      TableCell,
      Callout,
      BlockHandle,
      MediaEmbed.configure({ uploadFile }),
      ImageGallery.configure({ uploadFile }),
      FkTable,
      FkVideo,
      FkColumns,
      FkColumn,
      FkToggle,
      FkSteps,
      FkStep,
      FkEmbed,
      FkTabs,
      FkTab,
      FkButton,
      FkFile,
      FkKpi,
      FkCheck,
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      SlashCommands.configure({ uploadFile, pickDiskFiles }),
      // Collaboration + CollaborationCursor приходят готовыми из useCollab
      // (там же они и грузятся динамически). Идут последними: к этому моменту
      // схема собрана целиком, иначе y-prosemirror не отобразил бы наши
      // кастомные блоки (fk-table, шаги, колонки…).
      ...(liveExtensions ?? []),
    ],
    [placeholder, uploadFile, pickDiskFiles, liveExtensions],
  );

  const editor = useEditor({
    extensions,
    // При collab начальный текст приходит из Y.Doc: если задать content,
    // каждый клиент вставит свою копию и статья удвоится. Пустой Y.Doc
    // засеет ровно один участник — см. эффект ниже.
    content: live ? undefined : initialHtml,
    editable,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'tiptap wiki-content',
        spellcheck: 'true',
      },
      // Вставка/дроп картинки (Ctrl+V скриншота и т.п.): по умолчанию Tiptap
      // встраивает её как base64 прямо в HTML — это раздувает bodyMd и валит
      // лимит 200 000 символов. Вместо этого грузим файл через uploadFile
      // (attachments статьи → Object Storage/диск) и вставляем ссылку.
      handlePaste: (view, event, slice) => {
        if (uploadFile) {
          const files = imageFilesFrom(event.clipboardData);
          if (files.length > 0) {
            event.preventDefault();
            void uploadAndInsert(view, files, uploadFile);
            return true;
          }
        }
        // Не картинка. Ссылки на файлы вики и markdown-ссылки `[текст](url)` из text/plain —
        // настоящими ссылками/карточкой файла (см. paste-links.ts); остальное — обычная вставка.
        const dt = event.clipboardData;
        if (!dt) return false;
        try {
          const tr = buildLinkPasteTransaction(view.state, dt.getData('text/plain'), dt.getData('text/html'), slice);
          if (!tr) return false;
          event.preventDefault();
          view.dispatch(tr);
          return true;
        } catch {
          return false; // что-то пошло не так — пусть вставит сам редактор
        }
      },
      handleDrop: (view, event) => {
        if (!uploadFile) return false;
        const files = imageFilesFrom((event as DragEvent).dataTransfer);
        if (files.length === 0) return false;
        event.preventDefault();
        void uploadAndInsert(view, files, uploadFile);
        return true;
      },
    },
  });

  // Autosave heartbeat (опц.)
  useEffect(() => {
    if (!autosaveSec || !onAutosave || !editor) return;
    const t = setInterval(() => {
      onAutosave(editor.getHTML());
    }, autosaveSec * 1000);
    return () => clearInterval(t);
  }, [editor, autosaveSec, onAutosave]);

  // Отдаём инстанс наружу (ArticlePage применяет им AI-перепись). Колбэк
  // держим в ref: родитель обычно передаёт стрелку и без ref эффект
  // перезапускался бы на каждый рендер.
  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;
  // Свежий onChange для эффекта засева (иначе эффект перезапускался бы на каждый рендер родителя).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    onEditorReadyRef.current?.(editor ?? null);
    return () => onEditorReadyRef.current?.(null);
  }, [editor]);

  // Первичное наполнение пустого Y.Doc текстом статьи из БД. Сеет только
  // один участник (минимальный clientID) — иначе текст удвоится.
  useEffect(() => {
    if (!editor || !liveYdoc || !liveProvider) return;
    return seedCollabDoc(liveYdoc, liveProvider, () => {
      editor.commands.setContent(seedHtmlRef.current, false);
      // setContent без события (false) не зовёт onUpdate — родитель не узнал бы текст статьи
      // и считал редактор пустым (22.10.2026). Сообщаем явно.
      onChangeRef.current(editor.getHTML());
    });
  }, [editor, liveYdoc, liveProvider]);

  // Обновление контента извне (например после AI-генерации). При collab
  // не трогаем: истина — Y.Doc, а initialHtml здесь всегда «отстаёт» на
  // один onChange и затирал бы чужие правки. Замена текста в collab-режиме
  // идёт командой редактора (см. onEditorReady).
  useEffect(() => {
    if (live) return;
    if (editor && initialHtml !== editor.getHTML() && document.activeElement?.tagName !== 'DIV') {
      editor.commands.setContent(initialHtml, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHtml]);

  if (!editor) {
    return <div style={{ minHeight: 300, color: 'var(--text-muted)' }}>Загрузка редактора…</div>;
  }

  return (
    <div className="tiptap-wrapper" style={{ position: 'relative' }}>
      {editable && <TiptapToolbar editor={editor} uploadFile={uploadFile} />}
      {editable && <TiptapBubbleMenu editor={editor} />}
      {editable && <CalloutBubbleMenu editor={editor} />}
      <div
        style={{
          // Увеличенный left-padding (56px) даёт «жёлоб» под ховер-гаттер
          // «+/⠿», чтобы он не налезал на текст. top/right/bottom как было.
          padding: editable ? '24px 32px 48px 56px' : 0,
          minHeight: editable ? 400 : 100,
        }}
      >
        <EditorContent editor={editor} />
      </div>
      {editable && !pickDiskFilesProp && (
        <DiskFilePickerModal open={!!diskPicker} onCancel={() => finishDiskPicker([])} onPick={finishDiskPicker} />
      )}
    </div>
  );
}

export type { Editor };
